/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSearchParams } from 'react-router-dom';

import copy from 'copy-to-clipboard';

import { routes, RoutesType } from '../config';

import { AnyFunction } from 'types';

// We do it like this to be easier to define the type without tapping in
// the complex RoutesType type
type RouteParams<Route extends { getPath?: (params: any) => void; path: string }> =
  Route['getPath'] extends AnyFunction ? Parameters<Route['getPath']> : [];

interface ShareConfig {
  /**
   * `TRUE` by default
   */
  copyToClipboard?: boolean;

  /**
   * `false` by default
   *
   * Simply adds everything that is currently in the query.
   *
   * Only works if current path is the one you want to share
   */
  autofillQuery?: boolean;
}

/**
 *
 * @param targetRoute the route you want to copy the link to
 * @returns A function that created the link based on required/optional params
 */
export function useRouteShare<Target extends keyof RoutesType>(
  targetRoute: Target,
  { autofillQuery = false, copyToClipboard = true }: ShareConfig = {},
): (...p: RouteParams<RoutesType[Target]>) => string {
  const [query] = useSearchParams();

  const config = routes[targetRoute];

  return (...params: RouteParams<RoutesType[Target]>) => {
    const path =
      typeof (config as any).getPath === 'function'
        ? (config as any).getPath(...params)
        : config.path;

    const exitingParams = autofillQuery
      ? [path.includes('?') ? '&' : '?', query.toString()].join('')
      : '';

    const url = `${location.origin}${path}${exitingParams}`;

    if (copyToClipboard) copy(url);

    return url;
  };
}
